import { ActionContext } from "vuex";
import { isEqual } from "lodash";
import { notification } from "ant-design-vue";
import { fmtMsg } from "vue-glcommonui";
import { NetworkQualityPayload } from "@/store/room/interface";
import { TeacherRoomState } from "@/store/room/teacher/state";
import { HelperLocales } from "@/locales/localeid";
import { MainRoles, RemoteTeachingService } from "@/services";
import { CallingUserStatus } from "@/store/calling/mutations";
import { ConnectionDisconnectedReason, ConnectionState } from "agora-rtc-sdk-ng";
import { PostLocalUserReconnectedPayload } from "@/store/calling/actions";
import router from "@/router";
import { Paths } from "@/utils/paths";
import { delay, isReconnectedSuccess, isSameUserJoined, needTriggerManualReconnect } from "@/utils/utils";
import { notifySameUserJoined } from "@/utils/notifications";
import { MediaUserPublishStatus } from "@/store/calling/utils";
import { vuexName, MODULES } from "@/store/utils";
import { NetworkHealthTracker } from "@/agora/networkHealthTracker";

export const callingUtils = ({ commit, dispatch, state, rootState, getters }: ActionContext<TeacherRoomState, any>) => {
  //* Teacher joining class is "Teacher" or "Helper" */
  const { isHelper: joinAsHelper } = rootState.teacher;

  // Initialize new trackers per class session
  const teacherTracker = new NetworkHealthTracker();
  const studentTrackers = new Map<string, NetworkHealthTracker>();
  return {
    remoteUserJoined: async (id: string) => {
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.HANDLE_REMOTE_USER_JOINED, false), id);
    },
    remoteUserLeft: async (id: string) => {
      commit(vuexName(MODULES.CALLING.COMMITS.SET_CALLING_USER_IDS), { id, status: CallingUserStatus.LEFT }, { root: true });
      if (state.isSessionEnded || !state.info?.id || !rootState.teacher?.info.id) return;
      let remoteUserRole = MainRoles.TEACHER;
      const student = state.students.find((student) => student.id === id);
      if (student) {
        remoteUserRole = MainRoles.STUDENT;
        if (joinAsHelper && getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID, false)] === id) {
          await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.FORCE_OUT_HELPER_ONE_TO_ONE, false));
        }
      } else if (joinAsHelper) {
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHER_DISCONNECTED, false), true);
      } else {
        commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), false, { root: true });
        remoteUserRole = MainRoles.HELPER;
        notification.warn({ message: fmtMsg(HelperLocales.Disconnected, { name: state.helper?.name }) });
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.FORCE_OUT_HELPER_ONE_TO_ONE, false));
      }
      await RemoteTeachingService.putRemoteUserDisconnected({
        remoteUserRole,
        remoteUserId: id,
        sessionId: state.info.id,
        localUserId: rootState.teacher.info.id,
      });
    },
    localUserConnectionChanged: async (curState: ConnectionState, prevState: ConnectionState, reason: ConnectionDisconnectedReason | undefined) => {
      if (isSameUserJoined(curState, reason)) {
        if (state.manager) {
          await state.manager.handleKickOut();
        } else {
          await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.RESET_CLASS, false), false);
          notifySameUserJoined();
          await router.push(Paths.Teacher);
        }
        return;
      }
      const sessionId = state.info?.id;
      // localTeacherId can be teacher's id or helper's id
      const localTeacherId = rootState.teacher?.info?.id;
      if (!sessionId || !localTeacherId) return;
      if (isReconnectedSuccess(curState, prevState)) {
        if (state.manager) {
          // If the user is reconnected, leave channel to rejoin later
          await state.manager.agoraClient?.leaveChannel();
        }
        return;
      }
      commit(
        vuexName(MODULES.CALLING.COMMITS.SET_CALLING_USER_IDS),
        { id: localTeacherId, status: curState === "CONNECTED" ? CallingUserStatus.JOINED : CallingUserStatus.LEFT },
        { root: true },
      );
      await dispatch(
        `${
          curState === "CONNECTED" || curState === "CONNECTING"
            ? vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_ONLINE, false)
            : vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_OFFLINE, false)
        }`,
      );
      if (curState === "CONNECTED") {
        const payload: PostLocalUserReconnectedPayload = {
          localUserRole: joinAsHelper ? MainRoles.HELPER : MainRoles.TEACHER,
          localUserId: localTeacherId,
        };
        await dispatch(vuexName(MODULES.CALLING.DISPATCHES.POST_LOCAL_USER_RECONNECTED), payload, { root: true });
      }
      // Manually reconnecting the Agora channel to secure everything is up-to-date
      if (needTriggerManualReconnect(curState)) {
        if (state.manager) {
          if (state.manager.agoraClient.client.channelName) {
            // if the local user is in the channel, leave the channel and rejoin
            await state.manager.agoraClient?.leaveChannel();
          }
          // delay 2s to make sure the user has left the channel
          await delay(2000);
          // rejoin the channel to up-to-date the remote users
          await state.manager.manualReconnectAgoraRoom();
        }
      }
    },
    handleException: async () => {
      //
    },
    bandwidthUpdateBasedOnAgora: async (payload: NetworkQualityPayload) => {
      const { uplinkNetworkQuality, downlinkNetworkQuality } = payload;

      // 1. Teacher network
      const isTeacherWeak = teacherTracker.update(uplinkNetworkQuality, downlinkNetworkQuality);
      if (isTeacherWeak !== rootState.app.isLowBandWidth) {
        await dispatch(vuexName(MODULES.APP.DISPATCHES.SET_LOW_BAND_WIDTH, false), isTeacherWeak, { root: true });
      }

      // 2. Students' network
      const remoteQualities = state.manager?.agoraClient?._client?.getRemoteNetworkQuality();
      if (!remoteQualities) return;

      const newLowBandwidthStudents: string[] = [];
      for (const studentId in remoteQualities) {
        const { uplinkNetworkQuality, downlinkNetworkQuality } = remoteQualities[studentId];

        if (!studentTrackers.has(studentId)) {
          studentTrackers.set(studentId, new NetworkHealthTracker());
        }

        const tracker = studentTrackers.get(studentId)!;
        const isWeak = tracker.update(uplinkNetworkQuality, downlinkNetworkQuality);

        if (isWeak) {
          newLowBandwidthStudents.push(studentId);
        }
      }

      if (!isEqual(newLowBandwidthStudents.sort(), state.listStudentLowBandWidth.slice().sort())) {
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_LIST_STUDENT_LOW_BAND_WIDTH, false), newLowBandwidthStudents);
      }
    },
    handleUserPublishedVideo: async ({ id, status }: { id: string; status: MediaUserPublishStatus }) => {
      commit(vuexName(MODULES.CALLING.COMMITS.SET_VIDEO_USER_IDS), { id, status }, { root: true });
    },
    handleUserPublishedAudio: async ({ id, status }: { id: string; status: MediaUserPublishStatus }) => {
      commit(vuexName(MODULES.CALLING.COMMITS.SET_AUDIO_USER_IDS), { id, status }, { root: true });
    },
  };
};
